<!-- @format -->

<template>
  <div
    class="tir-scroll"
    :class="{
      showScroll: props.showOnHover,
    }"
    ref="$el"
  >
    <div
      class="hide-scroll"
      @scroll="onScroll"
      @transitionend="onTransitionend"
      ref="$scroll"
      :class="{
        'is-hidden': horizontalScrollbar.IsDrag || verticalScrollbar.IsDrag,
        'vertical-line': verticalScrollbar.Visible,
        'horizontal-line': horizontalScrollbar.Visible,
        'is-down': isDown,
        'enable-drag-scroll': enableDragScroll,
      }"
    >
      <slot />
    </div>

    <transition name="scroll-visible">
      <div
        :class="['vertical line', { 'is-left': isVerticalLeft }]"
        v-if="
          verticalScrollbar.Visible && visibleScrollbars.includes('vertical')
        "
      >
        <div
          class="scrollbar"
          :style="verticalScrollbar.Styles"
          @mousedown="onMousedownVerticalScrollbar"
        ></div>
      </div>
    </transition>

    <transition name="scroll-visible">
      <div
        class="horizontal line"
        v-if="
          horizontalScrollbar.Visible &&
          visibleScrollbars.includes('horizontal')
        "
      >
        <div
          class="scrollbar"
          :style="horizontalScrollbar.Styles"
          @mousedown="onMousedownHorizontalScrollbar"
        ></div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  reactive,
  ref,
  onMounted,
  onUnmounted,
  watch,
  PropType,
} from "vue";
import { VerticalScrollbarModel, HorizontalScrollbarModel } from "./models";
import { useAutoscroll, useScrollDrag } from "./composables";
import { AutoscrollOrientationEnum } from "./enums";
/**
 * Данные от родителя
 */
const props = defineProps({
  /**Отображении линии при наведении */
  showOnHover: {
    type: Boolean,
  },
  /**
   * Скролл по Y
   */
  y: { type: Number, default: null },
  /**
   * Скролл по X
   */
  x: { type: Number, default: null },
  /**
   * Вертикальный скролл слева
   */
  isVerticalLeft: { type: Boolean, default: false },
  /**
   * Видимые полосы прокрутки
   */
  visibleScrollbars: {
    type: Array as PropType<Array<"horizontal" | "vertical">>,
    default: ["horizontal", "vertical"],
  },
  /**
   * Включить скролл по драгу
   */
  enableDragScroll: { type: Boolean, default: false },
  /**Включить вертикальный автоскролл */
  enableVerticalAutoscroll: { type: Boolean, default: false },
  /**Включить горизонтальный автоскролл автоскролл */
  enableHorizontalAutoscroll: { type: Boolean, default: false },
  /**Отступ при котором срабатывает автоскролл (в процентах) */
  sensitivityArea: { type: Number, default: 5 },
});
/**
 * Генерация событий
 */
const emit = defineEmits(["onScroll", "update:y", "update:x"]);
/**
 * Элемент
 */
const $el = ref<HTMLElement>(null);
/**
 * Элемент со скролом
 */
const $scroll = ref<HTMLElement>(null);

const { isDown } = useScrollDrag(props.enableDragScroll, $el, $scroll);

useAutoscroll($scroll, $el, {
  sensitivityArea: computed(() => props.sensitivityArea),
  minSpeed: 0.1,
  maxSpeed: 25,
  acceleration: 0.5,
  interval: 16,
  direction: computed(() => {
    if (props.enableHorizontalAutoscroll && props.enableVerticalAutoscroll)
      return AutoscrollOrientationEnum.Both;
    if (props.enableHorizontalAutoscroll)
      return AutoscrollOrientationEnum.Horizontal;
    if (props.enableVerticalAutoscroll)
      return AutoscrollOrientationEnum.Vertical;
  }),
  emit: (event, value) => emit(event, value),
});

/**
 * Модели ползунков
 */
const verticalScrollbar = reactive(new VerticalScrollbarModel());
const horizontalScrollbar = reactive(new HorizontalScrollbarModel());
/**
 * Вызывается при скролле
 */
const onScroll = (e?: Event) => {
  verticalScrollbar.setStyles($scroll.value);
  horizontalScrollbar.setStyles($scroll.value);
  if (e) emit("onScroll", e);
};
/**
 * Отменить выделение текста
 */
const cancelUserSelect = () => {
  // Убираем все текущее выделение
  window.getSelection()?.removeAllRanges();
  document.body.classList.add("tir-scroll-user-select-none");
};

/**
 * При mousedown на горизонтальном скроллбаре
 */
const onMousedownHorizontalScrollbar = () => {
  horizontalScrollbar.onStartDrag();
  cancelUserSelect();
};
/**
 * При mousedown на вертикальном скроллбаре
 */
const onMousedownVerticalScrollbar = () => {
  verticalScrollbar.onStartDrag();
  cancelUserSelect();
};
/**
 * Вызывается при ресайзе, но с задержкой
 */
let timeout;
let debounce = (func, ms) => {
  return function () {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, arguments), ms);
  };
};
const debounceOnScroll = debounce(onScroll, 50);
/**
 * Вызывается при анимации внутри блока
 */
const onTransitionend = (e?: TransitionEvent) => {
  if (
    e &&
    e.propertyName &&
    (e.propertyName == "width" ||
      e.propertyName == "height" ||
      e.propertyName == "max-width" ||
      e.propertyName == "max-height" ||
      e.propertyName == "min-width" ||
      e.propertyName == "min-height")
  )
    debounceOnScroll();
};
/**
 * Вызывается при мутации
 */
const onObserve = () => {
  debounceOnScroll();
};
/**
 * Вызывается при изменении размера родительского элемента
 */
const onResizeObserve = () => {
  debounceOnScroll();
};
/**
 * Вызывается при ресайзе
 */
const onResize = () => {
  debounceOnScroll();
};
/**
 * Отслеживатель мутаций
 */
const observer: MutationObserver = new MutationObserver(onObserve);
/**
 * Отслеживатель изменения размера
 */
const resizeObserver: ResizeObserver = new ResizeObserver(onResizeObserve);
/**
 * Вызывается при движении мыши
 */
const onMousemove = (e: MouseEvent) => {
  verticalScrollbar.onDrag(e, $scroll.value);
  horizontalScrollbar.onDrag(e, $scroll.value);
};
/**
 * Вызывается при поднятии кнопки мыши
 */
const onMouseup = () => {
  verticalScrollbar.onStopDrag();
  horizontalScrollbar.onStopDrag();
  document.body.classList.remove("tir-scroll-user-select-none");
};
/**
 * Скролл по Y
 */
const y = computed(() => props.y);
/**
 * Установка скролла по Y
 */
const onY = () => {
  if (y.value || y.value == 0)
    verticalScrollbar.scrollTo(y.value, $scroll.value);
};
/**
 * Отслеживатель изменений Y
 */
watch(y, onY);
/**
 * Скролл по X
 */
const x = computed(() => props.x);
/**
 * Установка скролла по X
 */
const onX = () => {
  if (x.value || x.value == 0)
    horizontalScrollbar.scrollTo(x.value, $scroll.value);
};
/**
 * Отслеживатель изменений X
 */
watch(x, onX);
/**
 * При загрузке DOM
 */
onMounted(() => {
  onY();
  onX();
  debounceOnScroll();
  /**
   * Добавление слушателей
   */
  window.addEventListener("resize", onResize);
  window.addEventListener("mousemove", onMousemove);
  window.addEventListener("mouseup", onMouseup);
  window.addEventListener("dragend", onMouseup);
  observer.observe($el.value, {
    childList: true,
    subtree: true,
    attributes: true,
  });
  resizeObserver.observe($el.value);
});
/**
 * Перед исчезновением компонента
 */
onUnmounted(() => {
  /**
   * Удаление слушателей
   */
  window.removeEventListener("resize", onResize);
  window.removeEventListener("mousemove", onMousemove);
  window.removeEventListener("mouseup", onMouseup);
  window.removeEventListener("dragend", onMouseup);
  observer.disconnect();
  resizeObserver.disconnect();
});
/** Передача наверх */
defineExpose({
  $scroll,
});
</script>

<style lang="scss">
@import "./style.scss";
</style>
